import {
  type ReactNode,
} from 'react';
import {
  Callout,
  Facts,
  explainFailure,
} from '@affdice/portal-kit';
import {
  operatorFigureGroups,
  type OperatorFigureData,
} from '../screens.ts';
import { t } from '../messages.ts';
import type { Abilities } from './entity-actions.ts';
import { whyNoActions } from './entity-actions.ts';

export function Failure({ error }: { error: unknown }) {
  const shown = explainFailure(error);
  return (
    <Callout tone="error" {...(shown.reference ? { reference: shown.reference } : {})}>
      {shown.message}
    </Callout>
  );
}

/**
 * The operator's own dashboard, group for group — see `operatorFigureGroups`.
 * One component for every place this console shows an `overview()` read, so
 * an operator's Dashboard and one of its brands' Figures can never list
 * different things.
 */
export function OperatorFigures({ data }: { data: OperatorFigureData & { attribution_gap_because?: string | null } }) {
  return (
    <>
      {data.attribution_gap_because ? <Callout tone="caution">{data.attribution_gap_because}</Callout> : null}
      {operatorFigureGroups(data).map((g) => (
        <div key={g.title}>
          <h3 className="subhead">{g.title}</h3>
          <Facts items={g.figures.map((f) => ({ label: f.label, value: f.value }))} />
          {/* Batch 2 review, finding 5 — `.figures-note` is the provisional
              legend (DESIGN §4): only under a figure that can still move.
              The one note this group ever carries ("No averages yet…",
              `operatorFigureGroups`) explains missing data, not money in
              motion, so it takes `.panel-note` instead. */}
          {g.note ? <p className="panel-note">{g.note}</p> : null}
        </div>
      ))}
    </>
  );
}

/*
 * Row actions as glyphs rather than words.
 *
 * The drawing convention is the shared kit's own — a 24-unit box,
 * `currentColor`, 2-unit round strokes — so these do not read as coming from
 * a different set than the password field's show/hide eye. Local to this app
 * because these two are all anything here needs; a shared icon module is
 * worth having once there is a set to share.
 *
 * `aria-hidden` on the drawing and the name on the button around it. An icon
 * with no accessible name is unreachable with a screen reader and a guess
 * with a mouse, which is the entire cost of dropping the word.
 */
const ICON_STROKE = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

export function EditIcon() {
  return (
    <svg {...ICON_STROKE}>
      <path d="M12 20h9" />
      <path d="M16.4 3.6a2.1 2.1 0 0 1 3 3L7.5 18.5 3.5 19.5l1-4Z" />
    </svg>
  );
}

export function DeleteIcon() {
  return (
    <svg {...ICON_STROKE}>
      <path d="M3.5 6h17" />
      <path d="M8.5 6V4.5a1 1 0 0 1 1-1h5a1 1 0 0 1 1 1V6" />
      <path d="M18.5 6l-.9 13.1a2 2 0 0 1-2 1.9h-7.2a2 2 0 0 1-2-1.9L5.5 6" />
      <path d="M10 10.5v6" />
      <path d="M14 10.5v6" />
    </svg>
  );
}

export function IconButton({
  label,
  danger,
  disabled,
  onClick,
  children,
}: {
  /** Said out loud by a screen reader and shown on hover — never left implicit. */
  label: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`ref-icon-button${danger ? ' is-danger' : ''}`}
      aria-label={label}
      title={label}
      disabled={disabled === true}
      onClick={(e) => {
        // These sit in rows that open something of their own. A press meant
        // for this button must not also count as a press on the row, or
        // deleting opens a panel behind the confirmation it just raised.
        e.stopPropagation();
        onClick();
      }}
    >
      {children}
    </button>
  );
}

// §12.4 — mirrors the brand console's own stock light `--a`
// (`:root[data-portal='brand']`, portal-kit/src/styles.css), not a preset:
// `ACCENT_PRESETS` deliberately leaves orchid out (accent.ts's own comment —
// offering it there too gave a brand owner two ways to land on "the same
// colour" that then rendered differently in dark theme), which is a fact
// about what the picker offers, not about what a brand with no override of
// its own actually renders as. Looking it up by `nameKey` crashed this
// console on every load once orchid left the preset list.
export const STANDARD_BRAND_HEX = '#ad2b7c';

/**
 * A screen the menu already offers whose page is not built yet.
 *
 * Says what will be here and, where there is one, the way to reach the same
 * thing today, so a heading in the menu never opens onto a blank page.
 */
export function NotYetBuilt({
  title,
  what,
  meanwhile,
  action,
}: {
  title: string;
  /** What this screen will hold. */
  what: string;
  /** Where to find the same information until then, if anywhere. */
  meanwhile?: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <>
      <div className="page-head"><h1>{title}</h1></div>
      <Callout>
        <span className="stack">
          <span>{what}</span>
          {meanwhile ? <span>{meanwhile}</span> : null}
        </span>
      </Callout>
      {action ? (
        <div className="form-actions">
          <button type="button" className="copy" onClick={action.onClick}>{action.label}</button>
        </div>
      ) : null}
    </>
  );
}

/**
 * A translated sentence with words in bold.
 *
 * A message marks its bold words `**like this**`, so the emphasis travels with
 * the translation instead of being cut into fragments whose order only works
 * in English. Nothing else is interpreted: the text is never treated as HTML.
 */
export function Rich({ text }: { text: string }) {
  return (
    <>
      {text.split('**').map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}
    </>
  );
}

/**
 * The row of actions a detail view offers for the entity it is showing.
 *
 * Only what the entity's state and the viewer's permissions allow is passed
 * in as children; when that is nothing the row says why instead of standing
 * empty — the record is gone for good, the viewer may only look, or nothing
 * applies in the state it is in.
 */
export function ActionBar({
  count,
  purged,
  can,
  children,
}: {
  /** How many actions the model offered. */
  count: number;
  purged: boolean;
  can: Abilities;
  children: ReactNode;
}) {
  const why = whyNoActions(purged, can);
  return (
    <div className="row action-bar" role="group" aria-label={t('ent.actions.label')}>
      {count > 0 ? children : (
        <span className="faint">
          {why === 'purged' ? t('ent.actions.purged')
            : why === 'read-only' ? t('ent.actions.readOnly')
              : t('ent.actions.none')}
        </span>
      )}
    </div>
  );
}
